import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';
import GouvernanceOpenClawModal from '../components/GouvernanceOpenClawModal';
import AgenticAIModal from '../components/AgenticAIModal';
import GouvernanceAgentIAModal from '../components/GouvernanceAgentIAModal';
import GouvernanceAgenticAIModal from '../components/GouvernanceAgenticAIModal';
import GouvernanceComptesAIOAuthModal from '../components/GouvernanceComptesAIOAuthModal';
import TiersLogicielsAgentiquesModal from '../components/TiersLogicielsAgentiquesModal';
import PlanDeMatchIntegrationAgenticAIModal from '../components/RaciDeploiementAgentiqueModal';
import AikupCard from '../components/AikupCard';
import ExpertVisionModal from '../components/ExpertVisionModal';
import LeadersSection from '../components/LeadersSection';
import ZabbixAgent2InstallationModal from '../components/ZabbixAgent2InstallationModal';
import McpBzDepenseModal from '../components/McpBzDepenseModal';
import McpConnectionModal from '../components/McpConnectionModal';
import type { McpConnectionProfile } from '../components/McpConnectionModal';
import GoogleEmailAuthenticationModal from '../components/GoogleEmailAuthenticationModal';
import DatabaseAtlasIntroModal from '../components/DatabaseAtlasIntroModal';
import GoogleWorkspaceApiSetupModal from '../components/GoogleWorkspaceApiSetupModal';
import MayaInstallationModal from '../components/MayaInstallationModal';
import { agenticIAData } from '../data/programmationIAData';
import type { ProgrammationIAItem } from '../data/programmationIAData';

const iaAgenticLeaders = [
  {
    name: 'Dany Bourassa',
    avatar: '/DB.png',
    title: 'Président • Vision et innovation agentique',
    vision: 'Dany porte la vision du virage agentique de BZ et veille à ce que l’innovation demeure concrète, responsable et créatrice de valeur pour les clients.',
    attentes: 'Dany s’attend à une gouvernance claire, une responsabilité humaine assumée et des agents qui améliorent réellement la capacité de BZ et de ses clients.',
    pagePRIsME: 'La page IA agentic rassemble les cadres, standards et plans d’action nécessaires pour diriger ce virage de façon structurée.',
  },
  {
    name: 'Eric Durand',
    avatar: '/ED.png',
    title: 'Directeur de la programmation • Leader IA agentic',
    vision: 'Eric transforme la vision agentique en architectures, standards et solutions fiables, avec une approche directe, pragmatique et orientée résultats.',
    attentes: 'Eric s’attend à ce que chaque agent soit gouverné, sécurisé, documenté et intégré selon des standards techniques clairs et reproductibles.',
    pagePRIsME: 'La page IA agentic sert de référentiel pour concevoir, déployer et faire évoluer les agents IA de BZ avec rigueur.',
  },
];

const iaAgenticCardLeaders = iaAgenticLeaders.map(({ name, avatar, title }) => ({ name, avatar, title }));

const mayaInstallationItem: ProgrammationIAItem = {
  id: 32,
  title: 'Installation de Maya',
  category: 'Installation',
  description: 'Guide employé et bot pour ajouter Maya à Hermes, distinguer le fournisseur du catalogue WebGUI et préserver Codex par défaut.',
  aiSuggestion: 'Obtenir la clé auprès d’Éric ou Marco, sinon Dany; ne jamais exposer de secret et valider séparément CLI et WebGUI.',
  kpiScore: 0,
  imageUrl: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
  isCompleted: false,
  inKnowledgeBase: false,
};

const zabbixInstallationItem: ProgrammationIAItem = {
  id: 24,
  title: 'Installation Zabbix',
  category: 'Installation',
  description: 'Procédure interne BZ pour installer Zabbix Agent 2 7.4 en mode actif avec TLS-PSK unique, valider la réception des données et guider une exécution automatisée sécuritaire.',
  aiSuggestion: 'Suivre chaque étape dans l’ordre, ne jamais exposer la PSK et ne déclarer le succès qu’après réception de données récentes dans Zabbix.',
  kpiScore: 100,
  imageUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: true,
};

const googleEmailAuthenticationItem: ProgrammationIAItem = {
  id: 27,
  title: 'Configuration DKIM/DMARC/SPF',
  category: 'Installation',
  description: 'Procédure visuelle pour authentifier un sous-domaine Google Workspace avec DKIM, SPF et DMARC, publier les entrées DNS et valider le résultat de bout en bout.',
  aiSuggestion: 'Créer DKIM et SPF, confirmer leur réussite, puis seulement activer la politique DMARC stricte. Toujours relire le DNS public et tester un vrai courriel.',
  kpiScore: 100,
  imageUrl: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: true,
};

const googleWorkspaceApiSetupItem: ProgrammationIAItem = {
  id: 31,
  title: 'Configuration API Google Workspace',
  category: 'Installation',
  description: 'Procédure pour activer Gmail, Calendar, Drive et Docs dans Google Cloud, configurer OAuth 2.0 et créer un client sécurisé pour un nouvel environnement Google Workspace.',
  aiSuggestion: 'Activer uniquement les API et portées nécessaires, conserver le fichier JSON hors de Git et valider chaque service avec une opération réelle.',
  kpiScore: 100,
  imageUrl: 'https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: true,
};

const mcpBzDepenseItem: ProgrammationIAItem = {
  id: 26,
  title: 'MCP BZ Dépense',
  category: 'MCP',
  description: 'Procédure interne pour générer une clé autorisée, connecter Hermes Agent au serveur MCP BZ Dépense et valider la découverte sécurisée de ses outils.',
  aiSuggestion: 'Conserver la clé uniquement dans .env, référencer la variable dans config.yaml et confirmer la connexion avec hermes mcp test bz-depense.',
  kpiScore: 100,
  imageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: true,
};

const mcpCodeHubItem: ProgrammationIAItem = {
  id: 28,
  title: 'MCP Code Hub',
  category: 'MCP',
  description: 'Procédure interne pour connecter Hermes Agent au serveur MCP de Code Hub avec une clé conservée dans .env et une configuration YAML sécurisée.',
  aiSuggestion: 'Référencer BZ_CODEHUB_MCP_KEY sans exposer sa valeur, puis valider la découverte avec hermes mcp test bz-codehub.',
  kpiScore: 100,
  imageUrl: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: true,
};

const mcpBzVenteItem: ProgrammationIAItem = {
  id: 29,
  title: 'MCP BZ-vente',
  category: 'MCP',
  description: 'Procédure interne pour connecter Hermes Agent au serveur MCP de BZ-vente avec une clé conservée dans .env et une validation de bout en bout.',
  aiSuggestion: 'Référencer BZ_VENTES_MCP_KEY sans exposer sa valeur, puis valider la découverte avec hermes mcp test bzvente.',
  kpiScore: 100,
  imageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: true,
};

const mcpCodeHubProfile: McpConnectionProfile = {
  title: 'MCP Code Hub',
  serviceLabel: 'Code Hub',
  serverName: 'bz-codehub',
  url: 'https://codehub.bzinc.ca/api/mcp',
  environmentVariable: 'BZ_CODEHUB_MCP_KEY',
  purpose: 'Connecter Hermes Agent à Code Hub afin de rendre ses outils MCP disponibles dans les conversations et automatisations autorisées.',
};

const mcpBzVenteProfile: McpConnectionProfile = {
  title: 'MCP BZ-vente',
  serviceLabel: 'BZ-vente',
  serverName: 'bzvente',
  url: 'https://bzventes.bzinc.ca//api/mcp',
  environmentVariable: 'BZ_VENTES_MCP_KEY',
  purpose: 'Connecter Hermes Agent à BZ-vente afin de rendre ses outils MCP disponibles dans les conversations et automatisations autorisées.',
};

const agentiqueTasksItem: ProgrammationIAItem = {
  id: 25,
  title: 'Gestion demandes comité (Tâches)',
  category: 'Pilotage',
  description: 'Tableau compact type Excel pour suivre les demandes internes BZ liées aux bots : statut, priorité, responsables du comité, échéance et prochaine action.',
  aiSuggestion: 'Gardez le suivi simple : une ligne par demande, un responsable clair, une prochaine action concrète et des filtres rapides.',
  kpiScore: 95,
  imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: false,
};

const databaseAtlasItem: ProgrammationIAItem = {
  id: 30,
  title: 'Atlas des données BZ',
  category: 'Sécurité by-design',
  description: 'Cartographie frontend des databases et tables BZ pour clarifier les domaines métiers, les surfaces sensibles et les futurs contrats d’accès des bots en production.',
  aiSuggestion: 'Documenter avant d’ouvrir : domaine, propriétaire, sensibilité, accès permis et chemin de découpage avant toute automatisation agentique.',
  kpiScore: 98,
  imageUrl: 'https://images.unsplash.com/photo-1544383835-bda2bc66a55d?w=800&q=80',
  isCompleted: true,
  inKnowledgeBase: false,
};

export default function IAAgentic() {
  const navigate = useNavigate();
  const [selectedAgenticItem, setSelectedAgenticItem] = useState<ProgrammationIAItem | null>(null);
  const [expertVisionItem, setExpertVisionItem] = useState<ProgrammationIAItem | null>(null);
  const [isZabbixInstallationOpen, setIsZabbixInstallationOpen] = useState(false);
  const [isMayaInstallationOpen, setIsMayaInstallationOpen] = useState(false);
  const [isMcpBzDepenseOpen, setIsMcpBzDepenseOpen] = useState(false);
  const [selectedMcpProfile, setSelectedMcpProfile] = useState<McpConnectionProfile | null>(null);
  const [isGoogleEmailAuthenticationOpen, setIsGoogleEmailAuthenticationOpen] = useState(false);
  const [isDatabaseAtlasOpen, setIsDatabaseAtlasOpen] = useState(false);
  const [isGoogleWorkspaceApiSetupOpen, setIsGoogleWorkspaceApiSetupOpen] = useState(false);

  const closeAgenticModal = () => setSelectedAgenticItem(null);
  const isAgenticModalOpen = (id: number) => selectedAgenticItem?.id === id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header
        title="IA agentic"
        subtitle="Cadres, gouvernance et plans d’action pour déployer des agents IA de façon structurée, sécurisée et responsable."
      />

      <div className="max-w-7xl mx-auto px-8 pb-12">
        <LeadersSection leaders={iaAgenticLeaders} title="Nos leaders - IA agentic" />

        <div className="space-y-8">
            <section aria-labelledby="agentic-governance-title">
              <div className="mb-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-cyan-500 to-violet-500" />
                <h2 id="agentic-governance-title" className="px-3 text-2xl font-bold text-gray-800">
                  Gouvernance et cadres agentiques
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-cyan-500 to-violet-500" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AikupCard
                  item={agentiqueTasksItem}
                  onClick={() => navigate('/ia-agentic/taches')}
                  onExpertClick={() => undefined}
                  index={0}
                  customLeaders={iaAgenticCardLeaders}
                />
                <AikupCard
                  item={databaseAtlasItem}
                  onClick={() => setIsDatabaseAtlasOpen(true)}
                  onExpertClick={() => undefined}
                  index={1}
                  customLeaders={iaAgenticCardLeaders}
                />
                {agenticIAData.map((item, index) => (
                  <AikupCard
                    key={item.id}
                    item={item}
                    onClick={() => setSelectedAgenticItem(item)}
                    onExpertClick={() => undefined}
                    index={index + 2}
                    customLeaders={iaAgenticCardLeaders}
                    onLeaderClick={
                      item.id === 23
                        ? (leader) => {
                            if (leader.name === 'Dany Bourassa') setExpertVisionItem(item);
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </section>

            <section aria-labelledby="mcp-title">
              <div className="mb-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-cyan-500 to-emerald-500" />
                <h2 id="mcp-title" className="px-3 text-2xl font-bold text-gray-800">
                  MCP
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-cyan-500 to-emerald-500" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AikupCard
                  item={mcpBzDepenseItem}
                  onClick={() => setIsMcpBzDepenseOpen(true)}
                  onExpertClick={() => undefined}
                  index={0}
                  customLeaders={iaAgenticCardLeaders}
                />
                <AikupCard
                  item={mcpCodeHubItem}
                  onClick={() => setSelectedMcpProfile(mcpCodeHubProfile)}
                  onExpertClick={() => undefined}
                  index={1}
                  customLeaders={iaAgenticCardLeaders}
                />
                <AikupCard
                  item={mcpBzVenteItem}
                  onClick={() => setSelectedMcpProfile(mcpBzVenteProfile)}
                  onExpertClick={() => undefined}
                  index={2}
                  customLeaders={iaAgenticCardLeaders}
                />
              </div>
            </section>

            <section aria-labelledby="installation-title">
              <div className="mb-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500" />
                <h2 id="installation-title" className="px-3 text-2xl font-bold text-gray-800">
                  Installation
                </h2>
                <div className="h-px flex-1 bg-gradient-to-l from-emerald-500 to-cyan-500" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AikupCard
                  item={zabbixInstallationItem}
                  onClick={() => setIsZabbixInstallationOpen(true)}
                  onExpertClick={() => undefined}
                  index={0}
                  customLeaders={iaAgenticCardLeaders}
                />
                <AikupCard
                  item={googleEmailAuthenticationItem}
                  onClick={() => setIsGoogleEmailAuthenticationOpen(true)}
                  onExpertClick={() => undefined}
                  index={1}
                  customLeaders={iaAgenticCardLeaders}
                />
                <AikupCard
                  item={googleWorkspaceApiSetupItem}
                  onClick={() => setIsGoogleWorkspaceApiSetupOpen(true)}
                  onExpertClick={() => undefined}
                  index={2}
                  customLeaders={iaAgenticCardLeaders}
                />
                <AikupCard
                  item={mayaInstallationItem}
                  onClick={() => setIsMayaInstallationOpen(true)}
                  onExpertClick={() => undefined}
                  index={3}
                  customLeaders={iaAgenticCardLeaders}
                />
              </div>
            </section>

        </div>
      </div>

      <GouvernanceOpenClawModal isOpen={isAgenticModalOpen(16)} onClose={closeAgenticModal} />
      <AgenticAIModal isOpen={isAgenticModalOpen(18)} onClose={closeAgenticModal} />
      <GouvernanceAgentIAModal isOpen={isAgenticModalOpen(19)} onClose={closeAgenticModal} />
      <GouvernanceAgenticAIModal isOpen={isAgenticModalOpen(20)} onClose={closeAgenticModal} />
      <GouvernanceComptesAIOAuthModal isOpen={isAgenticModalOpen(21)} onClose={closeAgenticModal} />
      <TiersLogicielsAgentiquesModal isOpen={isAgenticModalOpen(22)} onClose={closeAgenticModal} />
      <PlanDeMatchIntegrationAgenticAIModal isOpen={isAgenticModalOpen(23)} onClose={closeAgenticModal} />
      <ZabbixAgent2InstallationModal
        isOpen={isZabbixInstallationOpen}
        onClose={() => setIsZabbixInstallationOpen(false)}
      />
      <MayaInstallationModal
        isOpen={isMayaInstallationOpen}
        onClose={() => setIsMayaInstallationOpen(false)}
      />
      <McpBzDepenseModal
        isOpen={isMcpBzDepenseOpen}
        onClose={() => setIsMcpBzDepenseOpen(false)}
      />
      <McpConnectionModal
        isOpen={selectedMcpProfile !== null}
        onClose={() => setSelectedMcpProfile(null)}
        profile={selectedMcpProfile}
      />
      <GoogleEmailAuthenticationModal
        isOpen={isGoogleEmailAuthenticationOpen}
        onClose={() => setIsGoogleEmailAuthenticationOpen(false)}
      />
      <DatabaseAtlasIntroModal
        isOpen={isDatabaseAtlasOpen}
        onClose={() => setIsDatabaseAtlasOpen(false)}
      />
      <GoogleWorkspaceApiSetupModal
        isOpen={isGoogleWorkspaceApiSetupOpen}
        onClose={() => setIsGoogleWorkspaceApiSetupOpen(false)}
      />
      <ExpertVisionModal
        isOpen={expertVisionItem !== null}
        onClose={() => setExpertVisionItem(null)}
        expert="dany"
        item={expertVisionItem}
      />
    </div>
  );
}
